import type {
  GetOneDiscount_content_discount,
  GetOneDiscount_content_discount_priceRules,
} from '@/queries/types/GetOneDiscount';

// Ring generations. Because this mirrors our pages, all generations prior to gen3 are lumped into the gen3 generation.
export const RingGenerations = ['gen3', 'gen4', 'gen4-ceramic'] as const;

// A type representing supported ring generations for a discount code.
export type SupportedRingGeneration = (typeof RingGenerations)[number];

// Price Rule Product Keys
const GEN4_PRODUCT_KEY_PREFIX = 'OuraRing4';
const GEN4_CER_PRODUCT_KEY_PREFIX = 'OuraRing4Cer';
const HORIZON_PRODUCT_KEY_PREFIX = 'Horizon';
const HERITAGE_PRODUCT_KEY_PREFIX = 'Heritage';

// Price Rule categories
const GEN4_CATEGORY = 'gen4-rings';
const GEN4_2024_CATEGORY = 'gen4-2024-rings';
const HORIZON_CATEGORY = 'horizon-rings';
const HERITAGE_CATEGORY = 'heritage-rings';
const RINGS_CATEGORY = 'rings';
const CERAMIC_CATEGORY = 'ceramic-rings';

// getSupportedPriceRuleGeneration takes a PriceRule and returns a set of SupportedRingGeneration.
// This set is the union of all Generations represented in productKeys and categories on the PriceRule.
const getSupportedPriceRuleGeneration = (
  priceRule: GetOneDiscount_content_discount_priceRules | null,
): Set<SupportedRingGeneration> => {
  const productKeys = priceRule?.productKeys;
  const supportedRingGenerations = new Set<SupportedRingGeneration>();
  if (productKeys && productKeys.length > 0) {
    productKeys.forEach((value) => {
      if (value) {
        if (
          value.startsWith(HERITAGE_PRODUCT_KEY_PREFIX) ||
          value.startsWith(HORIZON_PRODUCT_KEY_PREFIX)
        ) {
          supportedRingGenerations.add('gen3');
        } else if (value.startsWith(GEN4_CER_PRODUCT_KEY_PREFIX)) {
          supportedRingGenerations.add('gen4-ceramic');
        } else if (value.startsWith(GEN4_PRODUCT_KEY_PREFIX)) {
          supportedRingGenerations.add('gen4');
        }
      }
    });
  }

  const categories = priceRule?.categories;
  if (categories && categories.length > 0) {
    if (
      categories.includes(HERITAGE_CATEGORY) ||
      categories.includes(HORIZON_CATEGORY)
    ) {
      supportedRingGenerations.add('gen3');
    }
    if (categories.includes(CERAMIC_CATEGORY)) {
      supportedRingGenerations.add('gen4-ceramic');
    }
    if (
      categories.includes(GEN4_2024_CATEGORY) ||
      categories.includes(GEN4_CATEGORY)
    ) {
      supportedRingGenerations.add('gen4');
    }
    if (categories.includes(RINGS_CATEGORY)) {
      RingGenerations.map((generation: SupportedRingGeneration) =>
        supportedRingGenerations.add(generation),
      );
    }
  }

  return supportedRingGenerations;
};

// getSupportedDiscountGenerations takes a Discount and returns a set of SupportedRingGeneration.
// This set is the union of all Generations represented in productKeys and categories on the Discount.
export const getSupportedDiscountGenerations = (
  discount: GetOneDiscount_content_discount | null | undefined,
): Set<SupportedRingGeneration> => {
  let supportedDiscountGenerations = new Set<SupportedRingGeneration>();
  if (discount?.priceRules) {
    for (const priceRule of discount.priceRules) {
      const supportedPriceRuleGenerations =
        getSupportedPriceRuleGeneration(priceRule);
      supportedDiscountGenerations = new Set([
        ...supportedDiscountGenerations,
        ...supportedPriceRuleGenerations,
      ]);
    }
  }
  return supportedDiscountGenerations;
};
